import React, { useState, useEffect, useRef, useCallback } from 'react';
import { departmentsAPI, usersAPI, authAPI } from '../services/api';
import toast from 'react-hot-toast';
import { errorModal } from '../components/ErrorModal';
import { Department, PaginationInfo } from '../types';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Spinner from '../components/Spinner';
import Pagination from '../components/Pagination';
import { Search, ChevronDown, Trash2, Edit } from 'lucide-react';
import { confirmToast } from '../components/confirmToast';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  designation: string;
  invoice_permission?: string;
  phone?: string;
  department_name?: string;
  department_id?: number;
  employee_id?: string;
  is_active: boolean;
  is_verified: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
  mfa_enabled?: boolean | number;
  mfa_last_used?: string;
}

// interface UserPermissions {
//   role: string;
//   permissions: string[];
// }

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  // const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [isResettingMfa, setIsResettingMfa] = useState(false);
  const [resetQrCode, setResetQrCode] = useState<string | null>(null);
  const [isCheckingEmployeeId, setIsCheckingEmployeeId] = useState(false);
  const [employeeIdExists, setEmployeeIdExists] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Bulk upload modal states
  type SkippedRecord = { row: number; email: string; reason: string };
  const [showSkippedModal, setShowSkippedModal] = useState(false);
  const [skippedRecords, setSkippedRecords] = useState<SkippedRecord[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const permissionJson = [
    {
      value: 'admin',
      label: 'Admin'
    },
    {
      value: 'invoice_initiator',
      label: 'Initiator'
    },
    {
      value: 'invoice_approver',
      label: 'Approver'
    },
    {
      value: 'payment_approver',
      label: 'Payment Processor'
    },
    {
      value: 'finance_team',
      label: 'Finance Team'
    },
    {
      value: 'auditor',
      label: 'Auditor'
    }
  ]

  // Form states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [departmentDropdown, setDepartmentDropdown] = useState<Department[]>([]);
  
  // Searchable dropdown states for Create Modal
  const [departmentDropdownOpen, setDepartmentDropdownOpen] = useState(false);
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const [permissionDropdownOpen, setPermissionDropdownOpen] = useState(false);
  const [permissionSearchTerm, setPermissionSearchTerm] = useState('');
  const departmentDropdownRef = useRef<HTMLDivElement>(null);
  const permissionDropdownRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    designation: '',
    invoice_permission: '',
    phone: '',
    department_id: '',
    employee_id: '',
    is_active: true
  });

  // Validation states
  const [errors, setErrors] = useState({
    email: '',
    first_name: '',
    last_name: '',
    designation: '',
    phone: '',
    department_id: '',
    employee_id: ''
  });

  // Check if current user has permission - temporarily disabled for presentation
  // const hasPermission = (_permission: string): boolean => {
  //   return true; // Temporarily allow all permissions for presentation
  //   // return userPermissions?.permissions.includes(permission) || false;
  // };

  // Temporarily allow all permissions for presentation
  const canCreateUsers = true; // hasPermission('create_user');
  const canReadUsers = true; // hasPermission('read_users');
  const canUpdateUsers = true; // hasPermission('update_user');

  const fetchData = useCallback(async (page: number = pagination.page, search: string = searchTerm) => {
    try {
      setLoading(true);

      // // Fetch user permissions
      // const permissionsResponse = await usersAPI.getPermissions();
      // setUserPermissions(permissionsResponse.data);

      // Fetch users if user has permission
      if (canReadUsers) {
        const usersResponse = await usersAPI.getAll({
          page,
          limit: pagination.limit,
          search: search.trim() || undefined
        });
        setUsers(usersResponse.data);
        setPagination(usersResponse.pagination);
      }
    } catch (err: any) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, canReadUsers]);
  useEffect(() => {
    fetchData();
    fetchDepartmentDropdown();
  }, [fetchData]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when searching
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, searchTerm]);

  // Fetch users when search term or page changes
  useEffect(() => {
    fetchData();
  }, [searchTerm, pagination.page, fetchData]);

  // Handle clicks outside dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target as Node)) {
        setDepartmentDropdownOpen(false);
        setDepartmentSearchTerm('');
      }
      if (permissionDropdownRef.current && !permissionDropdownRef.current.contains(event.target as Node)) {
        setPermissionDropdownOpen(false);
        setPermissionSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchDepartmentDropdown = async () => {
    try {
      const response = await departmentsAPI.dropDown();
      if (response.success) {
        setDepartmentDropdown(response.data);
      } else {
        errorModal(response.message);
      }
    } catch (err: any) {
      console.error('Failed to fetch department dropdown:', err);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm(false)) {
      return;
    }

    setIsCreatingUser(true);
    try {
      const response = await usersAPI.create(formData);
      if (response.success) {
        toast.success(response.message);
      } else {
        errorModal(response.message);
      }

      // Reset form
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        designation: '',
        invoice_permission: '',
        phone: '',
        department_id: '',
        employee_id: '',
        is_active: true
      });
      // Clear errors when form is reset
      setErrors({
        email: '',
        first_name: '',
        last_name: '',
        designation: '',
        phone: '',
        department_id: '',
        employee_id: ''
      });
      // Reset employee ID check state
      setEmployeeIdExists(false);
      // Reset dropdown states
      setDepartmentDropdownOpen(false);
      setDepartmentSearchTerm('');
      setPermissionDropdownOpen(false);
      setPermissionSearchTerm('');
      fetchData()
      setShowCreateModal(false);
    } catch (err: any) {
      errorModal(err.response?.data?.error || 'Failed to create user');
      console.error('Failed to create user:', err);
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    // Validate form before submission
    if (!validateForm(true)) {
      return;
    }

    try {
      const response = await usersAPI.update(editingUser.id, formData);
      if (response.success) {
        toast.success(response.message);
      } else {
        errorModal(response.message);
      }
      setEditingUser(null);
      setShowEditModal(false);
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        designation: '',
        invoice_permission: '',
        phone: '',
        department_id: '',
        employee_id: '',
        is_active: true
      });
      // Clear errors when form is reset
      setErrors({
        email: '',
        first_name: '',
        last_name: '',
        designation: '',
        phone: '',
        department_id: '',
        employee_id: ''
      });
      fetchData()
    } catch (err: any) {
      errorModal(err.response?.data?.error || 'Failed to update user');
      console.error('Failed to update user:', err);
    }
  };


  // const handleRoleChange = async (userId: number, newRole: string) => {
  //   try {
  //     const response = await usersAPI.updateRole(userId, newRole);
  //     if (response.success) {
  //       toast.success(response.message);
  //     } else {
  //       errorModal(response.message);
  //     }
  //     fetchData()
  //   } catch (err: any) {
  //     errorModal(err.response?.data?.error || 'Failed to update user role');
  //     console.error('Failed to update user role:', err);
  //   }
  // };

  // Bulk upload handlers
  const handleBulkUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleBulkFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const response = await usersAPI.bulkUpload(file);
      if (response.success) {
        const createdCount = response.data?.created_count || 0;
        const skippedCount = response.data?.skipped_count || 0;
        toast.success(`Users processed. Created: ${createdCount}, Skipped: ${skippedCount}`);
        if (skippedCount > 0 && Array.isArray(response.data?.skipped)) {
          setSkippedRecords(response.data.skipped);
          setShowSkippedModal(true);
        } else {
          setSkippedRecords([]);
          setShowSkippedModal(false);
        }
        fetchData();
      } else {
        errorModal(response.message || 'Bulk upload failed');
      }
    } catch (err: any) {
      errorModal(err?.response?.data?.error || 'Failed to upload users');
      console.error('Failed to bulk upload users:', err);
    } finally {
      setIsUploading(false);
      // Clear input to allow re-uploading the same file
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Download sample template (headers only, no values)
  const downloadSample = () => {
    const headers = [
      'Email',
      'Employee ID',
      'First Name',
      'Last Name',
      'Department',
      'Designation',
      'Permission',
    ];
    const csvContent = headers.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'users_bulk_sample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const startEdit = (user: User) => {
    setEditingUser(user);
    setShowEditModal(true);
    setFormData({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      designation: user.designation,
      invoice_permission: user.invoice_permission || '',
      phone: user.phone || '',
      department_id: user.department_id?.toString() || '',
      employee_id: user.employee_id || '',
      is_active: user.is_active
    });
    setResetQrCode(null);
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setShowEditModal(false);
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      designation: '',
      invoice_permission: '',
      phone: '',
      department_id: '',
      employee_id: '',
      is_active: true
    });
    setResetQrCode(null);
    // Clear errors when canceling edit
    setErrors({
      email: '',
      first_name: '',
      last_name: '',
      designation: '',
      phone: '',
      department_id: '',
      employee_id: ''
    });
  };

  const handleResetMfa = async () => {
    if (!editingUser?.email) return;
    try {
      setIsResettingMfa(true);
      const res = await authAPI.resetMfaQr(editingUser.email);
      if (res?.success) {
        toast.success(res.message || 'MFA QR reset successfully');
        fetchData();
      } else {
        setResetQrCode(null);
        errorModal(res?.message || 'Failed to reset MFA');
      }
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) errorModal('User not found');
      else if (status === 401) errorModal('Account is deactivated');
      else if (status === 400) errorModal(error?.response?.data?.error || 'MFA already verified. Contact admin to disable or rotate.');
      else errorModal(error?.response?.data?.error || 'Failed to reset MFA');
    } finally {
      setIsResettingMfa(false);
      setShowEditModal(false);
    }
  };

  const cancelCreate = () => {
    setShowCreateModal(false);
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      designation: '',
      invoice_permission: '',
      phone: '',
      department_id: '',
      employee_id: '',
      is_active: true
    });
    // Clear errors when canceling create
    setErrors({
      email: '',
      first_name: '',
      last_name: '',
      designation: '',
      phone: '',
      department_id: '',
      employee_id: ''
    });
    // Reset employee ID check state
    setEmployeeIdExists(false);
    // Reset dropdown states
    setDepartmentDropdownOpen(false);
    setDepartmentSearchTerm('');
    setPermissionDropdownOpen(false);
    setPermissionSearchTerm('');
  };

  const getDesignationLabel = (designationValue: string): string => {
    return designationValue || 'N/A';
  };

  // Validation functions
  const validateForm = (isEditMode: boolean = false): boolean => {
    const newErrors = {
      email: '',
      first_name: '',
      last_name: '',
      designation: '',
      phone: '',
      department_id: '',
      employee_id: ''
    };

    let isValid = true;

    // Email validation with regex and domain check (only for create mode)
    if (!isEditMode) {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
        isValid = false;
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email.trim())) {
          newErrors.email = 'Please enter a valid email address';
          isValid = false;
        }  
        //   // Check if email domain is "pnbint.com"
        // else {
        //   const emailDomain = formData.email.trim().split('@')[1]?.toLowerCase();
        //   if (emailDomain !== 'pnbint.com') {
        //     newErrors.email = 'Email domain must be @pnbint.com';
        //     isValid = false;
        //   }
        // }
        
      }
    }

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
      isValid = false;
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
      isValid = false;
    }

    // Designation validation (only for create mode)
    if (!isEditMode && !formData.designation.trim()) {
      newErrors.designation = 'Designation is required';
      isValid = false;
    }


    // Department validation (mandatory for both add and edit)
    if (!formData.department_id) {
      newErrors.department_id = 'Department is required';
      isValid = false;
    }

    // Employee ID validation (mandatory for both add and edit)
    if (!formData.employee_id.trim()) {
      newErrors.employee_id = 'Employee ID is required';
      isValid = false;
    } else if (employeeIdExists) {
      newErrors.employee_id = 'Employee ID already exists';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const clearError = (field: string) => {
    setErrors(prev => ({
      ...prev,
      [field]: ''
    }));
  };

  const checkEmployeeIdExists = async (employeeId: string) => {
    if (!employeeId.trim()) {
      setEmployeeIdExists(false);
      return;
    }

    setIsCheckingEmployeeId(true);
    try {
      const response = await usersAPI.checkEmployeeId(employeeId.trim());
      if (response.success && response.data) {
        setEmployeeIdExists(response.data.exists);
        if (response.data.exists) {
          setErrors(prev => ({
            ...prev,
            employee_id: 'Employee ID already exists'
          }));
        } else {
          clearError('employee_id');
        }
      }
    } catch (err: any) {
      console.error('Failed to check employee ID:', err);
      // Don't show error to user, just log it
    } finally {
      setIsCheckingEmployeeId(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      clearError(field);
    }
    // Reset employee ID exists flag when user changes the value
    if (field === 'employee_id') {
      setEmployeeIdExists(false);
    }
  };

  const handleEmployeeIdBlur = () => {
    if (formData.employee_id.trim()) {
      checkEmployeeIdExists(formData.employee_id);
    }
  };

  const handleDeleteUser = async (userId: number) => {

    confirmToast('Are you sure you want to delete this user?', async () => {
      try {
        const response = await usersAPI.delete(userId);
        if (response.success) {
          toast.success(response.message);
          fetchData();
        } else {
          errorModal(response.message);
        }
      } catch (err: any) {
        errorModal(err.response?.data?.error || 'Failed to delete user');
        console.error('Failed to delete user:', err);
      }
    });
  };

  // Temporarily disable access check for presentation
  // if (!canReadUsers) {
  //   return (
  //     <div className="max-w-4xl mx-auto p-6">
  //       <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
  //         <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
  //         <p className="text-red-600">You don't have permission to view user management.</p>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="w-full px-4 py-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
        <p className="text-gray-600">Manage system users and their roles</p>
      </div>

      {/* Search and Create Section */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        
        {/* Search Input */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search users..."
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {searchInput && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Create User Button */}
        {canCreateUsers && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create New User
            </button>
            <button
              onClick={handleBulkUploadClick}
              disabled={isUploading}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${isUploading ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 text-white'}`}
            >
              {isUploading && <Spinner size="sm" color="gray" />}
              Bulk Upload
            </button>
            <button
              onClick={downloadSample}
              className="px-4 py-2 rounded-lg bg-yellow-300 hover:bg-yellow-200 text-black"
            >
              Download Sample
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleBulkFileChange}
              className="hidden"
            />
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create New User</h3>
              <button
                onClick={cancelCreate}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    disabled={isCreatingUser}
                    placeholder="Enter email"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.employee_id}
                      onChange={(e) => handleInputChange('employee_id', e.target.value)}
                      onBlur={handleEmployeeIdBlur}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.employee_id ? 'border-red-500' : 'border-gray-300'
                        }`}
                      disabled={isCreatingUser || isCheckingEmployeeId}
                      placeholder="Enter employee ID"
                    />
                    {isCheckingEmployeeId && (
                      <div className="absolute right-3 top-2.5">
                        <Spinner size="sm" color="gray" />
                      </div>
                    )}
                  </div>
                  {errors.employee_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.employee_id}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.first_name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    disabled={isCreatingUser}
                    placeholder="Enter first name"
                  />
                  {errors.first_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.last_name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    disabled={isCreatingUser}
                    placeholder="Enter last name"
                  />
                  {errors.last_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
                  <div ref={departmentDropdownRef} className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setDepartmentDropdownOpen(!departmentDropdownOpen);
                        setPermissionDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 flex items-center justify-between text-left ${errors.department_id ? 'border-red-500' : 'border-gray-300'
                        } ${isCreatingUser ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={isCreatingUser}
                    >
                      <span className={formData.department_id ? 'text-gray-900' : 'text-gray-500'}>
                        {formData.department_id
                          ? departmentDropdown.find(d => d.id.toString() === formData.department_id)?.department || 'Select Department'
                          : 'Select Department'}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${departmentDropdownOpen ? 'transform rotate-180' : ''}`} />
                    </button>
                    {departmentDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search departments..."
                              value={departmentSearchTerm}
                              onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {departmentDropdown
                            .filter(dept =>
                              dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                            )
                            .map((dept) => (
                              <button
                                key={dept.id}
                                type="button"
                                className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                                onClick={() => {
                                  handleInputChange('department_id', dept.id.toString());
                                  setDepartmentDropdownOpen(false);
                                  setDepartmentSearchTerm('');
                                }}
                              >
                                {dept.department}
                              </button>
                            ))}
                          {departmentDropdown.filter(dept =>
                            dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">
                              No departments found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.department_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.department_id}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Designation *</label>
                  <input
                    type="text"
                    value={formData.designation}
                    onChange={(e) => handleInputChange('designation', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.designation ? 'border-red-500' : 'border-gray-300'
                      }`}
                    disabled={isCreatingUser}
                    placeholder="Enter designation"
                  />
                  {errors.designation && (
                    <p className="mt-1 text-sm text-red-600">{errors.designation}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Permission</label>
                  <div ref={permissionDropdownRef} className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setPermissionDropdownOpen(!permissionDropdownOpen);
                        setDepartmentDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 flex items-center justify-between text-left ${isCreatingUser ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={isCreatingUser}
                    >
                      <span className={formData.invoice_permission ? 'text-gray-900' : 'text-gray-500'}>
                        {formData.invoice_permission
                          ? permissionJson.find(p => p.value === formData.invoice_permission)?.label || 'Select Permission'
                          : 'Select Permission'}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${permissionDropdownOpen ? 'transform rotate-180' : ''}`} />
                    </button>
                    {permissionDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search permissions..."
                              value={permissionSearchTerm}
                              onChange={(e) => setPermissionSearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {permissionJson
                            .filter(permission =>
                              permission.label.toLowerCase().includes(permissionSearchTerm.toLowerCase())
                            )
                            .map((permission) => (
                              <button
                                key={permission.value}
                                type="button"
                                className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                                onClick={() => {
                                  setFormData({ ...formData, invoice_permission: permission.value });
                                  setPermissionDropdownOpen(false);
                                  setPermissionSearchTerm('');
                                }}
                              >
                                {permission.label}
                              </button>
                            ))}
                          {permissionJson.filter(permission =>
                            permission.label.toLowerCase().includes(permissionSearchTerm.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">
                              No permissions found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <PhoneInput
                    country={'gb'}
                    value={formData.phone}
                    onChange={(value) => handleInputChange('phone', value)}
                    inputStyle={{
                      width: '100%',
                      height: '42px',
                      border: errors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      paddingLeft: '48px'
                    }}
                    buttonStyle={{
                      backgroundColor: '#f9fafb',
                      border: errors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '6px 0 0 6px'
                    }}
                    dropdownStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px'
                    }}
                    preferredCountries={['us', 'gb', 'ca', 'au', 'in']}
                    placeholder="Enter phone number"
                    disabled={isCreatingUser}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={cancelCreate}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg"
                  disabled={isCreatingUser}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  disabled={isCreatingUser}
                >
                  {isCreatingUser && <Spinner size="sm" color="white" />}
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit User: {editingUser.email}</h3>
              <button
                onClick={cancelEdit}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID *</label>
                  <input
                    type="text"
                    value={formData.employee_id}
                    onChange={(e) => handleInputChange('employee_id', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.employee_id ? 'border-red-500' : 'border-gray-300'
                      }`}
                    disabled={isCreatingUser}
                  />
                  {errors.employee_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.employee_id}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.first_name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    disabled={isCreatingUser}
                  />
                  {errors.first_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.last_name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    disabled={isCreatingUser}
                  />
                  {errors.last_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
                  <select
                    value={formData.department_id}
                    onChange={(e) => handleInputChange('department_id', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.department_id ? 'border-red-500' : 'border-gray-300'
                      }`}
                    disabled={isCreatingUser}
                  >
                    <option value="">Select Department</option>
                    {departmentDropdown.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.department}
                      </option>
                    ))}
                  </select>
                  {errors.department_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.department_id}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Designation *</label>
                  <input
                    type="text"
                    value={formData.designation}
                    onChange={(e) => handleInputChange('designation', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.designation ? 'border-red-500' : 'border-gray-300'
                      }`}
                    disabled={isCreatingUser}
                    placeholder="Enter designation"
                  />
                  {errors.designation && (
                    <p className="mt-1 text-sm text-red-600">{errors.designation}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Permission</label>
                  <select
                    value={formData.invoice_permission}
                    onChange={(e) => setFormData({ ...formData, invoice_permission: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={isCreatingUser}
                  >
                    <option value="">Select Permission</option>
                    {permissionJson.map((permission) => (
                      <option key={permission.value} value={permission.value}>
                        {permission.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <PhoneInput
                    country={'us'}
                    value={formData.phone}
                    onChange={(value) => handleInputChange('phone', value)}
                    inputStyle={{
                      width: '100%',
                      height: '42px',
                      border: errors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      paddingLeft: '48px'
                    }}
                    buttonStyle={{
                      backgroundColor: '#f9fafb',
                      border: errors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '6px 0 0 6px'
                    }}
                    dropdownStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px'
                    }}
                    preferredCountries={['us', 'gb', 'ca', 'au', 'in']}
                    placeholder="Enter phone number"
                    disabled={isCreatingUser}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                      className="flex items-center space-x-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
                      disabled={isCreatingUser}
                    >
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                        formData.is_active 
                          ? 'bg-primary-600 border-primary-600' 
                          : 'bg-white border-gray-300'
                      }`}>
                        {formData.is_active && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className={`text-sm font-medium ${
                        formData.is_active ? 'text-gray-900' : 'text-gray-500'
                      }`}>
                        {formData.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </button>
                  </div>
                </div> */}
                {editingUser?.mfa_enabled ? (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reset MFA</label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleResetMfa}
                      disabled={isResettingMfa}
                      className={`px-4 py-2 rounded-lg ${isResettingMfa ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
                    >
                      {isResettingMfa ? <Spinner size="sm" color="white" /> : 'Reset MFA QR'}
                    </button>
                    {resetQrCode && (
                      <img src={resetQrCode} alt="MFA QR" className="h-24 w-24 border rounded" />
                    )}
                    </div>
                </div>
                ) : null}
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg"
                >
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Users ({pagination.total})
            {searchTerm && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                - Filtered results
              </span>
            )}
          </h3>
        </div>
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Spinner size="lg" color="blue" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                    Designation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    MFA
                  </th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th> */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      {searchTerm ? 'No users found matching your search.' : 'No users found. Create your first user to get started.'}
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">
                      {user.employee_id || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                      {/* <div className="text-xs text-gray-400 mt-1 space-x-2">
                        {user.invoice_permission && (
                          <span className={`inline-block text-xs px-2 py-1 rounded-full ${user.invoice_permission === 'invoice_initiator'
                            ? 'bg-yellow-100 text-yellow-800'
                            : user.invoice_permission === 'invoice_approver' ? 'bg-primary-100 text-primary-800' : user.invoice_permission === 'payment_approver' ? 'bg-green-100 text-green-800' : user.invoice_permission === 'auditor' ? 'bg-purple-100 text-purple-800' : user.invoice_permission === 'admin' ? 'bg-blue-100 text-blue-800' : user.invoice_permission === 'finance_team' ? 'bg-orange-100 text-orange-800' : ''
                            }`}>
                            {permissionJson.find((permission) => permission.value === user.invoice_permission)?.label}
                          </span>
                        )}
                      </div> */}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                    {user.invoice_permission && (
                          <span className={`inline-block text-xs px-2 py-1 rounded-full ${user.invoice_permission === 'invoice_initiator'
                            ? 'bg-yellow-100 text-yellow-800'
                            : user.invoice_permission === 'invoice_approver' ? 'bg-primary-100 text-primary-800' : user.invoice_permission === 'payment_approver' ? 'bg-green-100 text-green-800' : user.invoice_permission === 'auditor' ? 'bg-purple-100 text-purple-800' : user.invoice_permission === 'admin' ? 'bg-blue-100 text-blue-800' : user.invoice_permission === 'finance_team' ? 'bg-orange-100 text-orange-800' : ''
                            }`}>
                            {permissionJson.find((permission) => permission.value === user.invoice_permission)?.label}
                          </span>
                        )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {user.department_name || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-block w-48 truncate text-sm text-gray-900" title={getDesignationLabel(user.designation)}>
                      {getDesignationLabel(user.designation)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.mfa_enabled
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-700'
                      }`}>
                      {user.mfa_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  {/* <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                        }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {user.is_verified && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                          Verified
                        </span>
                      )}
                    </div>
                  </td> */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      {canUpdateUsers && (
                        <button
                          onClick={() => startEdit(user)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      
                    </div>
                  </td>
                </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Skipped Records Modal */}
      {showSkippedModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Skipped Records ({skippedRecords.length})</h3>
              <button
                onClick={() => setShowSkippedModal(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {skippedRecords.length === 0 ? (
              <div className="text-sm text-gray-600">No skipped records.</div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Row</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {skippedRecords.map((rec, idx) => (
                      <tr key={`${rec.email}-${rec.row}-${idx}`}>
                        <td className="px-4 py-2 text-sm text-gray-700">{rec.row}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{rec.email}</td>
                        <td className="px-4 py-2 text-sm text-red-700">{rec.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowSkippedModal(false)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      <Pagination
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        loading={loading}
      />
    </div>
  );
};

export default UserManagement; 